import { Link } from "react-router-dom";

export default function About() {
  return (
    <div className="max-w-[1100px] mx-auto px-6 py-12">
      <div className="border-b border-black/15 pb-6 mb-10">
        <div className="overline">/methodology</div>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter mt-2">How this works</h1>
      </div>

      <div className="grid lg:grid-cols-12 gap-10">
        <article className="lg:col-span-7 space-y-6 text-[17px] leading-relaxed text-[#222]">
          <p className="text-2xl font-display">
            Receipts is a citation-first, evidence-first record of every government action that meaningfully
            shapes women's rights in the United States — both expansions and restrictions.
          </p>
          <h2 className="font-display text-2xl font-bold mt-8">What qualifies as an entry</h2>
          <p>
            An entry must describe a specific action by a federal or state body — Congress, a state legislature, an
            executive agency, the White House, a federal or state supreme court, a regulator. Editorials, opinion
            polling, and unattributed rumors do not qualify.
          </p>
          <h2 className="font-display text-2xl font-bold mt-8">Citations are mandatory</h2>
          <p>
            Every entry carries at least one citation. We prefer primary documents: bill text, court opinions,
            agency rules, signed orders, and floor votes. Where reporting is needed for context, we link
            established outlets and quote them verbatim.
          </p>
          <h2 className="font-display text-2xl font-bold mt-8">Neutrality of language</h2>
          <p>
            Entry language is plain and descriptive. We don't editorialize; we let the receipts do the arguing.
            Each entry is tagged with an <em>impact</em> direction — restricts, protects, or mixed — based on the
            action's effect on the legal status quo, not on partisan affiliation.
          </p>
          <h2 className="font-display text-2xl font-bold mt-8">AI assistance, human gate</h2>
          <p>
            Editors use Claude Sonnet 4.5 to extract structured drafts from primary documents and reporting. The AI never publishes anything itself. Every AI-assisted output enters the system as a <em>draft</em> with an explicit "AI-assisted" flag and a list of unverified citations.
          </p>
          <p>
            An entry can only become public when:
          </p>
          <ul className="list-disc pl-6 space-y-1">
            <li>A human editor opens each citation, reads the source, and ticks "Verified" — recording the editor's identity and timestamp on every receipt.</li>
            <li>All citations on the entry are verified.</li>
            <li>An editor explicitly clicks <em>Publish</em>.</li>
          </ul>
          <p>
            Editing the citations on an already-published entry automatically sends it back into <em>pending review</em>; it disappears from the public site until a human re-verifies and re-publishes. The editorial state of every entry — drafted by, reviewed by, verified at — is part of the audit trail.
          </p>
          <h2 className="font-display text-2xl font-bold mt-8">Errors and corrections</h2>
          <p>
            If an entry is wrong, tell us. <Link to="/submit" className="underline underline-offset-4">Submit a correction</Link> with a citation and we will revise — transparently — within seven days.
          </p>
        </article>

        <aside className="lg:col-span-5">
          <div className="bg-white border border-black/15">
            <img
              src="https://images.unsplash.com/photo-1775144657626-29ff0a46ca90?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1Mjh8MHwxfHNlYXJjaHwxfHxsZWdhbCUyMGRvY3VtZW50cyUyMGxhdyUyMGJvb2tzJTIwZGVza3xlbnwwfHx8fDE3Nzg0MjkxNzN8MA&ixlib=rb-4.1.0&q=85"
              alt="Legal documents and law books"
              className="w-full h-72 object-cover grayscale"
            />
            <div className="p-5">
              <div className="overline">/the editorial standard</div>
              <p className="mt-3 text-sm">
                Receipts is independent. It does not accept paid placements. It does not endorse candidates or parties.
                It cites, and lets the public draw conclusions.
              </p>
            </div>
          </div>
        </aside>
      </div>

      {/* Data access — feeds, exports, API */}
      <div className="mt-16 border-t border-black/15 pt-10">
        <div className="overline mb-2">/data access</div>
        <h2 className="font-display text-3xl font-bold">Use this data</h2>
        <p className="text-base text-[#333] mt-3 max-w-2xl">
          The full set of published entries is available to researchers, journalists, and
          accountability projects — no API key needed.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-black/15 border border-black/15 mt-6">
          <DataLink href="/api/feed.xml" label="RSS feed" sub="Subscribe in any reader" />
          <DataLink href="/api/feed.json" label="JSON Feed 1.1" sub="Machine-readable subscription" />
          <DataLink href="/api/export/entries?fmt=csv" label="CSV export" sub="Open in Excel / Sheets" />
          <DataLink href="/api/export/entries?fmt=json" label="JSON export" sub="Full structured data" />
        </div>
      </div>
    </div>
  );
}

function DataLink({ href, label, sub }) {
  return (
    <a href={href} target="_blank" rel="noreferrer" className="bg-white p-5 hover:bg-black hover:text-white transition group" data-testid={`data-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="font-display text-xl font-bold">{label}</div>
      <div className="font-mono-plex text-xs uppercase tracking-wider text-[#555] group-hover:text-white/70 mt-1">{sub}</div>
    </a>
  );
}
